import { useEffect, useRef, useState } from "react";

/**
 * Reveal — wrapper scroll-reveal / cascade on-load (DESIGN-SPEC 3.4).
 *
 * Landing-only. Applique la classe `.reveal` (etat initial opacity:0 +
 * translateY, defini dans index.css / index.html §2) et expose la variable
 * de stagger `--i` consommee par `transition-delay: calc(var(--i) * 60ms)`.
 * Un IntersectionObserver natif ajoute `.is-in` a l'entree, une seule fois
 * (puis `unobserve` si `once`).
 *
 * Accessibilite / 0 CLS :
 * - Sous `prefers-reduced-motion: reduce`, l'element est rendu visible
 *   immediatement (etat `inView` initial = true, aucun observer cree). La
 *   neutralisation visuelle finale est assuree par le fallback CSS §2
 *   (`.reveal { opacity:1 !important; transform:none !important }`).
 * - Le wrapper rend TOUJOURS son element et n'altere que des classes /
 *   opacity / transform : la geometrie est reservee, pas de collapse => 0 CLS.
 *
 * props : { as?: ElementType (def "div"), index?: number (stagger, def 0),
 *           once?: boolean (def true), className?, children, ...rest }
 */
export default function Reveal({
  as: Tag = "div",
  index = 0,
  once = true,
  className = "",
  children,
  ...rest
}) {
  const ref = useRef(null);

  // Reduced-motion : visible d'emblee, aucun observer (cf. fallback CSS §2).
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const [inView, setInView] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const node = ref.current;
    if (!node) return;

    // Pas d'IntersectionObserver (vieux navigateurs) -> visible directement.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, prefersReducedMotion]);

  const classes = ["reveal", inView ? "is-in" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag ref={ref} className={classes} style={{ "--i": index }} {...rest}>
      {children}
    </Tag>
  );
}
